/**
 * PitchSettingsContext — localStorage-backed visual settings for the investor pitch deck.
 * Provides per-section overrides for font size, text color, padding, etc.
 * Used by PitchSection and individual section components, and edited via the admin page.
 */
import { createContext, useContext, useState, useCallback } from "react";

const STORAGE_KEY = "pitch_visual_settings";

/** Default settings — every section inherits from `global` unless overridden. */
const defaultSettings = {
  global: {
    baseFontSize: 16,          // px — applied to body text
    headingScale: 1,           // multiplier for heading sizes
    bodyTextColor: "text-gray-300",
    mutedTextColor: "text-gray-400",
    accentColor: "#3B82F6",
    cardBg: "bg-[#0F1826]/60",
    cardBorder: "border-white/8",
    sectionPaddingX: "px-4 sm:px-8",
    maxWidth: "max-w-6xl",
  },
  sections: {
    // Per-section overrides (merged on top of global).
    // Keys match section ids: hero, problem, solution, businessModel, market,
    // traction, unitEconomics, competitive, team, roadmap, risks, ask
  },
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return deepMerge(defaultSettings, parsed);
    }
  } catch { /* ignore corrupt data */ }
  return defaultSettings;
}

function saveSettings(settings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

/** Simple deep merge (objects only, arrays replaced). */
function deepMerge(target, source) {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === "object"
    ) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}

const PitchSettingsContext = createContext(null);

export function PitchSettingsProvider({ children }) {
  const [settings, setSettingsState] = useState(loadSettings);

  const updateGlobal = useCallback((patch) => {
    setSettingsState((prev) => {
      const next = { ...prev, global: { ...prev.global, ...patch } };
      saveSettings(next);
      return next;
    });
  }, []);

  const updateSection = useCallback((sectionId, patch) => {
    setSettingsState((prev) => {
      const next = {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionId]: { ...(prev.sections[sectionId] || {}), ...patch },
        },
      };
      saveSettings(next);
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSettingsState(defaultSettings);
  }, []);

  const resetSection = useCallback((sectionId) => {
    setSettingsState((prev) => {
      const sections = { ...prev.sections };
      delete sections[sectionId];
      const next = { ...prev, sections };
      saveSettings(next);
      return next;
    });
  }, []);

  return (
    <PitchSettingsContext.Provider
      value={{ settings, updateGlobal, updateSection, resetAll, resetSection, defaultSettings }}
    >
      {children}
    </PitchSettingsContext.Provider>
  );
}

export function usePitchSettings() {
  const ctx = useContext(PitchSettingsContext);
  if (!ctx) throw new Error("usePitchSettings must be used within PitchSettingsProvider");
  return ctx;
}

/** Helper: get the effective value for a section (section override > global fallback). */
export function useEffectiveSetting(sectionId, key) {
  const { settings } = usePitchSettings();
  return settings.sections?.[sectionId]?.[key] ?? settings.global[key];
}
